import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const apple = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";
const ORANGE = "#ff6b00";
const ORANGE_DEEP = "#e85f00";
const GREEN = "#10b981";

/* ============================================================ */
/*  ENTRY                                                       */
/* ============================================================ */
export default function RYDriverApp() {
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem("ry-driver-state");
      return saved ? JSON.parse(saved) : { auth: "in", onboardingStep: 0 };
    } catch {
      return { auth: "in", onboardingStep: 0 };
    }
  });

  useEffect(() => {
    try { localStorage.setItem("ry-driver-state", JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  const setAuth = (auth) => setState((s) => ({ ...s, auth }));
  const nextOb = () => setState((s) => ({ ...s, onboardingStep: s.onboardingStep + 1 }));
  const prevOb = () => setState((s) => ({ ...s, onboardingStep: Math.max(0, s.onboardingStep - 1) }));
  const finishOnboarding = () => setState({ auth: "in", onboardingStep: 0 });
  const logout = () => setState({ auth: "splash", onboardingStep: 0 });

  return (
    <div style={{ height: "100%", background: "#0a0a0a", color: "white", fontFamily: apple, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <AnimatePresence mode="wait">
        {state.auth === "splash" && <Splash key="splash" onSignIn={() => setAuth("login")} onCreate={() => setAuth("onboarding")} />}
        {state.auth === "login" && <Login key="login" onSubmit={() => setAuth("in")} onCreate={() => setAuth("onboarding")} onBack={() => setAuth("splash")} />}
        {state.auth === "onboarding" && <Onboarding key="onb" step={state.onboardingStep} next={nextOb} back={prevOb} done={finishOnboarding} exit={() => setAuth("splash")} />}
        {state.auth === "in" && <MainApp key="main" onLogout={logout} />}
      </AnimatePresence>
    </div>
  );
}

/* ============================================================ */
/*  SPLASH                                                      */
/* ============================================================ */
function Splash({ onSignIn, onCreate }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ height: 54 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 32px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 30%, rgba(255,107,0,0.18), transparent 60%)" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: 3, color: ORANGE, fontWeight: 700 }}>RY · DRIVER</div>
          <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: -1.5, marginTop: 12, lineHeight: 1.02 }}>
            Find loads.<br/>Bid quickly.<br/>Get paid.
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginTop: 16, lineHeight: 1.5, maxWidth: 280 }}>
            The freight marketplace for drivers and carriers across Canada and the US.
          </div>
        </div>
      </div>
      <div style={{ padding: "0 24px 36px", display: "flex", flexDirection: "column", gap: 10 }}>
        <PrimaryBtn onClick={onCreate}>Create driver account</PrimaryBtn>
        <SecondaryBtn onClick={onSignIn}>Sign in</SecondaryBtn>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", textAlign: "center", marginTop: 4 }}>By continuing you agree to RY Xpress Terms</div>
      </div>
    </motion.div>
  );
}

/* ============================================================ */
/*  LOGIN                                                       */
/* ============================================================ */
function Login({ onSubmit, onCreate, onBack }) {
  const [email, setEmail] = useState("marcus@whitaker.co");
  const [pw, setPw] = useState("••••••••");
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ height: 54 }} />
      <div style={{ padding: "8px 22px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={onBack} style={iconBtn} data-testid="login-back">‹</button>
        <div style={{ fontSize: 11, letterSpacing: 2, color: ORANGE, fontWeight: 700 }}>RY · DRIVER</div>
        <div style={{ width: 30 }} />
      </div>
      <div style={{ padding: "0 24px" }}>
        <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: -0.6, marginTop: 12 }}>Welcome back</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>Sign in to access loads.</div>
        <Field label="EMAIL" value={email} onChange={setEmail} type="email" testid="login-email" />
        <Field label="PASSWORD" value={pw} onChange={setPw} type="password" testid="login-password" />
        <button style={{ background: "none", border: "none", color: ORANGE, fontSize: 13, padding: 0, marginTop: 6, cursor: "pointer", fontFamily: apple }}>Forgot password?</button>
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ padding: "0 24px 36px", display: "flex", flexDirection: "column", gap: 10 }}>
        <PrimaryBtn onClick={onSubmit} testid="login-submit">Sign in</PrimaryBtn>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", textAlign: "center" }}>
          New to RY Xpress?{" "}
          <button onClick={onCreate} style={{ background: "none", border: "none", color: ORANGE, fontSize: 13, cursor: "pointer", fontFamily: apple, fontWeight: 600 }}>Create account</button>
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================================ */
/*  ONBOARDING                                                  */
/* ============================================================ */
const obSteps = [
  { id: "personal", title: "Tell us about you", sub: "Account basics" },
  { id: "license", title: "Driver's licence", sub: "We verify automatically" },
  { id: "insurance", title: "Insurance", sub: "Cargo + liability" },
  { id: "truck", title: "Your rig", sub: "Truck & trailer details" },
  { id: "photos", title: "Equipment photos", sub: "3 quick angles" },
  { id: "regions", title: "Service regions", sub: "Where you'll run" },
  { id: "preferences", title: "Load preferences", sub: "What you haul best" },
  { id: "banking", title: "Banking", sub: "How we pay you" },
  { id: "submitted", title: "Under review", sub: "We'll be in touch" },
];

function Onboarding({ step, next, back, done, exit }) {
  const s = obSteps[step];
  const isLast = step === obSteps.length - 1;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ height: 54 }} />
      <div style={{ padding: "8px 22px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={step === 0 ? exit : back} style={iconBtn}>‹</button>
        <div style={{ fontSize: 11, letterSpacing: 1.5, color: "rgba(255,255,255,0.6)" }}>STEP {step + 1} of {obSteps.length}</div>
        <div style={{ width: 30 }} />
      </div>
      <div style={{ padding: "0 24px 14px", display: "flex", gap: 3 }}>
        {obSteps.map((_, i) => (
          <div key={i} style={{ flex: 1, height: 3, background: i <= step ? ORANGE : "rgba(255,255,255,0.1)", borderRadius: 2 }} />
        ))}
      </div>
      <div style={{ padding: "8px 24px 0" }}>
        <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.6 }}>{s.title}</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>{s.sub}</div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "18px 24px 24px" }}>
        <AnimatePresence mode="wait">
          <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22 }}>
            {s.id === "personal" && <ObPersonal />}
            {s.id === "license" && <ObUpload label="DRIVER'S LICENCE" hint="Front & back, clear photos" />}
            {s.id === "insurance" && <ObInsurance />}
            {s.id === "truck" && <ObTruck />}
            {s.id === "photos" && <ObPhotos />}
            {s.id === "regions" && <ObRegions />}
            {s.id === "preferences" && <ObPreferences />}
            {s.id === "banking" && <ObBanking />}
            {s.id === "submitted" && <ObSubmitted />}
          </motion.div>
        </AnimatePresence>
      </div>
      <div style={{ padding: "0 24px 28px" }}>
        <PrimaryBtn onClick={isLast ? done : next} testid={`ob-next-${s.id}`}>
          {isLast ? "Enter app" : step === obSteps.length - 2 ? "Submit application" : "Continue"}
        </PrimaryBtn>
      </div>
    </motion.div>
  );
}

function ObPersonal() {
  const [accountType, setAccountType] = useState("oo");
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
        <Pill active={accountType === "oo"} onClick={() => setAccountType("oo")} label="Owner-Operator" />
        <Pill active={accountType === "co"} onClick={() => setAccountType("co")} label="Company / Fleet" />
      </div>
      <Field label="FIRST NAME" value="Dwayne" />
      <Field label="LAST NAME" value="Whitaker" />
      <Field label="EMAIL" value="marcus@whitaker.co" />
      <Field label="MOBILE" value="+1 (416) 555-0148" />
      {accountType === "co" && <Field label="COMPANY NAME" value="" placeholder="Whitaker Transport Ltd." />}
    </>
  );
}

function ObUpload({ label, hint }) {
  const [uploaded, setUploaded] = useState(false);
  return (
    <>
      <button onClick={() => setUploaded(true)} style={{ background: "rgba(255,255,255,0.04)", border: `1.5px dashed ${uploaded ? GREEN : "rgba(255,255,255,0.15)"}`, borderRadius: 16, padding: "26px 14px", width: "100%", color: "white", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: uploaded ? "rgba(16,185,129,0.18)" : "rgba(255,107,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
          {uploaded ? "✓" : "📄"}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{uploaded ? "Uploaded · verifying" : `Upload ${label}`}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{hint}</div>
      </button>
      <Field label="LICENCE #" value="ON-CDL-4882-19A" />
      <Field label="EXPIRY" value="2029-04-18" />
    </>
  );
}

function ObInsurance() {
  return (
    <>
      <PolicyCard label="Cargo Insurance" carrier="Northbridge" limit="$100,000" />
      <PolicyCard label="Auto Liability" carrier="Northbridge" limit="$1,000,000" />
      <PolicyCard label="General Liability" carrier="Travelers" limit="$2,000,000" />
      <div style={{ marginTop: 4, padding: 12, background: "rgba(16,185,129,0.08)", border: "0.5px solid rgba(16,185,129,0.3)", borderRadius: 12, fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
        ✓ FMCSA SAFER check auto-runs on every submission
      </div>
    </>
  );
}
function PolicyCard({ label, carrier, limit }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: 12, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{carrier} · {limit}</div>
      </div>
      <span style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.65)", fontSize: 10, letterSpacing: 1, padding: "4px 8px", borderRadius: 6 }}>UPLOAD</span>
    </div>
  );
}

function ObTruck() {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="YEAR" value="2022" />
        <Field label="MAKE" value="Kenworth" />
      </div>
      <Field label="MODEL" value="T680" />
      <Field label="VIN" value="1XKYDP9X4NJ123456" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="TRAILER TYPE" value="Dry Van 53'" />
        <Field label="CAPACITY" value="45,000 lb" />
      </div>
      <Field label="FEATURES" value="Liftgate · Pallet jack · E-track" />
    </>
  );
}

function ObPhotos() {
  const angles = ["Front", "Side", "Trailer"];
  const [done, setDone] = useState([false, false, false]);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
      {angles.map((a, i) => (
        <button key={a} onClick={() => setDone(d => d.map((v, j) => j === i ? true : v))} style={{ aspectRatio: "1", background: done[i] ? "linear-gradient(135deg, rgba(255,107,0,0.22), rgba(255,107,0,0.05))" : "rgba(255,255,255,0.04)", border: `1.5px dashed ${done[i] ? ORANGE : "rgba(255,255,255,0.15)"}`, borderRadius: 14, color: "white", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 11 }}>
          <span style={{ fontSize: 20 }}>{done[i] ? "✓" : "📷"}</span>
          <span style={{ fontWeight: 600 }}>{a}</span>
        </button>
      ))}
    </div>
  );
}

function ObRegions() {
  const all = ["Ontario", "Quebec", "BC", "Alberta", "Northeast US", "Midwest US", "South US", "Texas", "California"];
  const [picked, setPicked] = useState(["Ontario", "Northeast US", "Midwest US"]);
  const toggle = (r) => setPicked(p => p.includes(r) ? p.filter(x => x !== r) : [...p, r]);
  return (
    <>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 10 }}>{picked.length} selected</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {all.map(r => <Pill key={r} label={r} active={picked.includes(r)} onClick={() => toggle(r)} />)}
      </div>
    </>
  );
}

function ObPreferences() {
  const types = ["Dry Van", "Reefer", "Flatbed", "Stepdeck", "Power Only", "Hazmat", "Oversize"];
  const [picked, setPicked] = useState(["Dry Van", "Reefer"]);
  const toggle = (t) => setPicked(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);
  return (
    <>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 10 }}>Pick the load types you accept</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {types.map(t => <Pill key={t} label={t} active={picked.includes(t)} onClick={() => toggle(t)} />)}
      </div>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 8 }}>Minimum payout per load</div>
      <Field label="MIN. PAYOUT" value="$800" />
    </>
  );
}

function ObBanking() {
  return (
    <>
      <Field label="ACCOUNT HOLDER" value="Dwayne Johnson" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="TRANSIT" value="00482" />
        <Field label="INSTITUTION" value="003" />
      </div>
      <Field label="ACCOUNT" value="••••••• 4148" />
      <div style={{ marginTop: 4, padding: 12, background: "rgba(255,107,0,0.06)", border: "0.5px solid rgba(255,107,0,0.3)", borderRadius: 12, fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
        Same-day payouts available for 1.5% per transfer
      </div>
    </>
  );
}

function ObSubmitted() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "30px 10px 10px", textAlign: "center" }}>
      <div style={{ width: 84, height: 84, borderRadius: "50%", background: "rgba(255,107,0,0.18)", border: `2px solid ${ORANGE}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>⏳</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 16, letterSpacing: -0.3 }}>Application submitted</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 10, lineHeight: 1.5, maxWidth: 260 }}>
        RY Xpress will review your documents and verify carrier compliance. Most approvals land within <strong style={{ color: "white" }}>24 hours</strong>.
      </div>
      <div style={{ marginTop: 22, padding: "10px 14px", background: "rgba(16,185,129,0.08)", border: "0.5px solid rgba(16,185,129,0.3)", borderRadius: 999, fontSize: 11, color: GREEN, letterSpacing: 1, fontWeight: 600 }}>
        ✓ DEMO MODE · CONTINUE TO APP
      </div>
    </div>
  );
}

/* ============================================================ */
/*  MAIN APP                                                    */
/* ============================================================ */
const seedLoads = [
  { id: "L1", from: "Toronto, ON", to: "Detroit, MI", fromShort: "TO", toShort: "DET", trailer: "Dry Van", km: 540, lbs: "12,000 lbs", pickup: "Today, 2:00 PM", pickupDate: "May 15, 2025", delivery: "Tomorrow, 8:00 AM", deliveryDate: "May 16, 2025", distAway: "5.2 mi", price: 2950, ceiling: 3200, current: 2950, weight: "12,000 lb", mi: 336, commodity: "Auto parts", instructions: "Dock #14." },
  { id: "L2", from: "Hamilton, ON", to: "Buffalo, NY", fromShort: "HAM", toShort: "BUF", trailer: "Reefer", km: 295, lbs: "8,500 lbs", pickup: "Today, 4:30 PM", pickupDate: "May 15, 2025", delivery: "Today, 11:00 PM", deliveryDate: "May 15, 2025", distAway: "12 mi", price: 1780, ceiling: 1980, current: 1780, weight: "8,500 lb", mi: 183, commodity: "Frozen produce", instructions: "Keep at -2°C." },
  { id: "L3", from: "Mississauga, ON", to: "Montreal, QC", fromShort: "MIS", toShort: "MTL", trailer: "Dry Van", km: 620, lbs: "20,000 lbs", pickup: "Tomorrow, 7:00 AM", pickupDate: "May 16, 2025", delivery: "May 17, 6:00 AM", deliveryDate: "May 17, 2025", distAway: "18 mi", price: 2300, ceiling: 2500, current: 2300, weight: "20,000 lb", mi: 385, commodity: "Consumer goods", instructions: "Touch-free pickup." },
  { id: "L4", from: "London, ON", to: "Columbus, OH", fromShort: "LON", toShort: "COL", trailer: "Dry Van", km: 610, lbs: "11,000 lbs", pickup: "Today, 3:00 PM", pickupDate: "May 15, 2025", delivery: "Tomorrow, 7:00 AM", deliveryDate: "May 16, 2025", distAway: "22 mi", price: 2875, ceiling: 3100, current: 2875, weight: "11,000 lb", mi: 379, commodity: "Industrial parts", instructions: "Forklift on site." },
  { id: "L5", from: "Kitchener, ON", to: "Pittsburgh, PA", fromShort: "KIT", toShort: "PIT", trailer: "Flatbed", km: 570, lbs: "18,000 lbs", pickup: "May 16, 8:00 AM", pickupDate: "May 16, 2025", delivery: "May 17, 8:00 AM", deliveryDate: "May 17, 2025", distAway: "30 mi", price: 2450, ceiling: 2680, current: 2450, weight: "18,000 lb", mi: 354, commodity: "Steel beams", instructions: "Tarps required." },
  { id: "L6", from: "Windsor, ON", to: "Indianapolis, IN", fromShort: "WIN", toShort: "IND", trailer: "Reefer", km: 740, lbs: "16,000 lbs", pickup: "May 16, 5:00 PM", pickupDate: "May 16, 2025", delivery: "May 18, 8:00 AM", deliveryDate: "May 18, 2025", distAway: "35 mi", price: 3100, ceiling: 3380, current: 3100, weight: "16,000 lb", mi: 460, commodity: "Dairy", instructions: "Maintain temp log." },
];

function MainApp({ onLogout }) {
  const [tab, setTab] = useState("home");
  const [online, setOnline] = useState(true);
  const [loads, setLoads] = useState(seedLoads);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [activeLoad, setActiveLoad] = useState({ id: "78432", from: "Toronto, ON", to: "Chicago, IL", fromShort: "TO", toShort: "CHI", mi: 527, ceiling: 1640, current: 1540, trailer: "Reefer", weight: "28,400 lb", pickup: "Today · 14:00", commodity: "Frozen produce", instructions: "Keep at -2°C. Lumper $80." });
  const [activeStep, setActiveStep] = useState(3);
  const [bids, setBids] = useState([]);
  const [toast, setToast] = useState(null);
  const [balance, setBalance] = useState(2340);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2200);
  };

  const submitBid = (load, amount) => {
    setBids((b) => [...b.filter(x => x.id !== load.id), { id: load.id, lane: `${load.fromShort} → ${load.toShort}`, bid: amount, status: "LEADING", color: GREEN, load }]);
    setSelectedLoad(null);
    setActiveLoad(load);
    setActiveStep(0);
    setTab("active");
    showToast(`Bid submitted at $${amount.toLocaleString()}`);
  };

  const cashOut = () => {
    if (balance <= 0) { showToast("Nothing to cash out"); return; }
    showToast(`$${balance.toLocaleString()} on its way · ETA 30 min`);
    setBalance(0);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ height: 54 }} />
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
        {tab === "home" && <HomeTab online={online} setOnline={setOnline} loads={loads} activeLoad={activeLoad} setTab={setTab} setSelectedLoad={setSelectedLoad} showToast={showToast} />}
        {tab === "loads" && <LoadsTab loads={loads} onPick={setSelectedLoad} online={online} setOnline={setOnline} setTab={setTab} showToast={showToast} />}
        {tab === "active" && <ActiveTab activeLoad={activeLoad} step={activeStep} setStep={setActiveStep} online={online} setOnline={setOnline} setTab={setTab} showToast={showToast} />}
        {tab === "earnings" && <EarningsTab balance={balance} cashOut={cashOut} online={online} setOnline={setOnline} setTab={setTab} showToast={showToast} />}
        {tab === "profile" && <ProfileTab onLogout={onLogout} online={online} setOnline={setOnline} setTab={setTab} showToast={showToast} />}
      </div>
      <BottomNav tab={tab} setTab={setTab} hasActive={!!activeLoad} />
      <AnimatePresence>
        {selectedLoad && <LoadDetail load={selectedLoad} onClose={() => setSelectedLoad(null)} onSubmitBid={submitBid} />}
      </AnimatePresence>
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: "absolute", top: 64, left: "50%", transform: "translateX(-50%)", background: "rgba(20,20,22,0.96)", backdropFilter: "blur(20px)", border: `0.5px solid ${ORANGE}50`, color: "white", padding: "8px 14px", borderRadius: 999, fontSize: 12, fontFamily: apple, fontWeight: 600, zIndex: 100, boxShadow: "0 10px 30px rgba(0,0,0,0.5)", maxWidth: "85%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            <span style={{ color: ORANGE, marginRight: 6 }}>●</span>{toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ---------- BOTTOM NAV ---------- */
function BottomNav({ tab, setTab, hasActive }) {
  const tabs = [
    { id: "home", label: "Home", icon: <NavIcon path="M3 11l9-7 9 7v9a2 2 0 01-2 2h-4v-7h-6v7H5a2 2 0 01-2-2z" /> },
    { id: "loads", label: "Loads", icon: <NavIcon path="M3 7h13l3 4v6H3z M5 19a2 2 0 100-4 2 2 0 000 4z M17 19a2 2 0 100-4 2 2 0 000 4z" /> },
    { id: "active", label: "Active", icon: <NavIcon path="M9 4l-1 2H4v14h16V6h-4l-1-2z M12 11l-3 3h2v3h2v-3h2z" />, badge: hasActive ? "1" : null },
    { id: "earnings", label: "Earnings", icon: <NavIcon path="M2 7h20v10H2z M2 11h20 M6 14h3 M16 14h2" /> },
    { id: "profile", label: "Profile", icon: <NavIcon path="M12 12a4 4 0 100-8 4 4 0 000 8zm-7 9a7 7 0 0114 0z" /> },
  ];
  return (
    <div style={{ borderTop: "0.5px solid rgba(255,255,255,0.08)", background: "rgba(10,10,10,0.95)", backdropFilter: "blur(20px)", padding: "8px 0 22px", display: "flex" }}>
      {tabs.map((t) => {
        const active = tab === t.id;
        return (
          <motion.button
            key={t.id}
            whileTap={{ scale: 0.92 }}
            onClick={() => setTab(t.id)}
            data-testid={`nav-${t.id}`}
            style={{ flex: 1, background: "none", border: "none", color: active ? ORANGE : "rgba(255,255,255,0.45)", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: 4, position: "relative" }}
          >
            <div style={{ width: 22, height: 22, position: "relative" }}>
              {t.icon}
              {t.badge && <span style={{ position: "absolute", top: -4, right: -8, minWidth: 14, height: 14, padding: "0 4px", borderRadius: 999, background: "#ef4444", color: "white", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #0a0a0a" }}>{t.badge}</span>}
            </div>
            <div style={{ fontSize: 10, fontWeight: 600 }}>{t.label}</div>
          </motion.button>
        );
      })}
    </div>
  );
}
function NavIcon({ path }) {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg>;
}

/* ---------- HOME ---------- */
function HomeTab({ online, setOnline, loads, activeLoad, setTab, setSelectedLoad, showToast }) {
  const pickLoad = (id) => setSelectedLoad(loads.find(l => l.id === id) || loads[0]);
  return (
    <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0 10px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 26, fontWeight: 900, color: ORANGE, letterSpacing: -0.5, fontStyle: "italic" }}>RY</span>
          <span style={{ fontSize: 17, fontWeight: 700, color: "white", letterSpacing: 3 }}>DRIVER</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => showToast("2 new notifications")} data-testid="notification-bell" style={{ background: "none", border: "none", color: "white", cursor: "pointer", position: "relative", padding: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
            <span style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: ORANGE, border: "1.5px solid #0a0a0a" }} />
          </button>
          <button onClick={() => setTab("profile")} style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #6b7280, #374151)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "white", border: "1.5px solid rgba(255,255,255,0.1)", cursor: "pointer" }}>M</button>
        </div>
      </div>

      {/* Greeting + Online toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "4px 0 14px" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "white", letterSpacing: -0.3 }}>Good morning, Dwayne</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>Drive safe today!</div>
        </div>
        <button
          onClick={() => setOnline(!online)}
          data-testid="online-toggle"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "0.5px solid rgba(255,255,255,0.12)",
            color: "white",
            padding: "5px 8px 5px 10px",
            borderRadius: 999,
            cursor: "pointer",
            fontFamily: apple,
            fontSize: 11,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: online ? GREEN : "rgba(255,255,255,0.4)" }} />
          {online ? "Online" : "Offline"}
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>▾</span>
        </button>
      </div>

      {/* 4 stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        <MiniStat icon="📦" value="12" label="Loads Nearby" action="View all" onClick={() => setTab("loads")} />
        <MiniStat icon="🚚" value="1" label="Active Load" action="View details" onClick={() => setTab("active")} />
        <MiniStat icon="💰" value="$850" label="Today's Earnings" action="View" onClick={() => setTab("earnings")} />
        <MiniStat icon="📈" value="$4,920" label="This Week" action="View" onClick={() => setTab("earnings")} />
      </div>

      {/* Active Load Card */}
      <div style={{ marginTop: 12, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 14, position: "relative", overflow: "hidden" }}>
        {/* Truck image overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 160,
            height: "100%",
            background: "linear-gradient(90deg, rgba(15,15,15,0.95) 0%, rgba(15,15,15,0.4) 30%, transparent 100%), url('https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&q=70') center/cover",
            opacity: 0.85,
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: ORANGE, fontWeight: 700, letterSpacing: 1.5 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: ORANGE }} />
            ACTIVE LOAD
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, marginTop: 8, color: "white", letterSpacing: -0.3 }}>
            Toronto, ON <span style={{ color: "rgba(255,255,255,0.5)", margin: "0 6px" }}>→</span> Chicago, IL
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12, maxWidth: 240 }}>
            <div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>PICK UP</div>
              <div style={{ fontSize: 12, color: "white", marginTop: 2 }}>Today, 2:00 PM</div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>STATUS</div>
              <div style={{ fontSize: 12, color: GREEN, marginTop: 2, fontWeight: 600 }}>In Transit</div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>DELIVERY</div>
              <div style={{ fontSize: 12, color: "white", marginTop: 2 }}>Tomorrow, 8:00 AM</div>
            </div>
            <div>
              <div style={{ display: "inline-block", padding: "3px 8px", border: "0.5px solid rgba(16,185,129,0.4)", borderRadius: 6, marginTop: 12 }}>
                <span style={{ fontSize: 10, color: GREEN, fontFamily: "monospace" }}>Load ID: 78432</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 14, position: "relative", zIndex: 2 }}>
            <button onClick={() => setTab("active")} data-testid="home-active-view" style={{ flex: 1.2, background: ORANGE, color: "white", border: "none", padding: "9px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
              <span>🛻</span> View Load
            </button>
            <button onClick={() => showToast("Opening route in Maps…")} data-testid="home-active-route" style={{ flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.15)", padding: "9px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
              <span style={{ fontSize: 10 }}>📍</span> Open Route
            </button>
            <button onClick={() => showToast("Dispatch chat opened")} data-testid="home-active-message" style={{ flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.15)", padding: "9px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
              <span style={{ fontSize: 10 }}>💬</span> Message
            </button>
          </div>
        </div>
      </div>

      {/* Available loads */}
      <div style={{ marginTop: 16, background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700 }}>AVAILABLE LOADS NEAR YOU</div>
          <button onClick={() => setTab("loads")} style={{ background: "none", border: "none", color: ORANGE, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>View all</button>
        </div>
        {[
          { id: "a1", from: "Toronto, ON", to: "Detroit, MI", trailer: "Dry Van", km: "540 km", lbs: "12,000 lbs", pickup: "Today, 4:30 PM", price: "$2,950" },
          { id: "a2", from: "Hamilton, ON", to: "Buffalo, NY", trailer: "Reefer", km: "295 km", lbs: "8,500 lbs", pickup: "Today, 3:00 PM", price: "$1,780" },
          { id: "a3", from: "Mississauga, ON", to: "Montreal, QC", trailer: "Flatbed", km: "620 km", lbs: "20,000 lbs", pickup: "Tomorrow, 7:00 AM", price: "$2,300" },
        ].map((l, i, arr) => (
          <div key={l.id} style={{ padding: "10px 0", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "white" }}>{l.from} <span style={{ color: "rgba(255,255,255,0.4)" }}>→</span> {l.to}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
                  <span>🚚</span> {l.trailer}<span style={{ opacity: 0.4 }}>•</span>{l.km}<span style={{ opacity: 0.4 }}>•</span>{l.lbs}
                </div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>Pick up: {l.pickup}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)" }}>Starting at</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "white" }}>{l.price}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
              <button onClick={() => pickLoad(["L1", "L2", "L3"][i])} style={{ background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.1)", padding: "5px 14px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>Details</button>
              <button onClick={() => pickLoad(["L1", "L2", "L3"][i])} style={{ background: ORANGE, color: "white", border: "none", padding: "5px 16px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: apple }}>Bid</button>
            </div>
          </div>
        ))}
      </div>

      {/* Recommended */}
      <div style={{ marginTop: 12, background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(255,107,0,0.12)", border: "0.5px solid rgba(255,107,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: ORANGE }}>★</div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700 }}>RECOMMENDED FOR YOU</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>Based on your equipment and location</div>
          </div>
        </div>
        <button onClick={() => pickLoad("L4")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginTop: 12, paddingTop: 10, borderTop: "0.5px solid rgba(255,255,255,0.06)", background: "none", border: "none", color: "white", cursor: "pointer", fontFamily: apple }}>
          <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "white" }}>London, ON <span style={{ color: "rgba(255,255,255,0.4)" }}>→</span> Columbus, OH</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
              <span>🚚</span> Dry Van<span style={{ opacity: 0.4 }}>•</span>610 km<span style={{ opacity: 0.4 }}>•</span>11,000 lbs
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 10, color: GREEN, fontWeight: 700, display: "flex", alignItems: "center", gap: 2 }}>High Match ⌃</div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)" }}>Starting at</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "white" }}>$2,875</div>
            </div>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 16 }}>›</span>
          </div>
        </button>
      </div>

      {/* Alerts */}
      <div style={{ marginTop: 12, background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700 }}>ALERTS</div>
            <span style={{ background: "#ef4444", color: "white", fontSize: 9, fontWeight: 700, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>2</span>
          </div>
          <button onClick={() => showToast("2 active alerts")} data-testid="home-alerts-viewall" style={{ background: "none", border: "none", color: ORANGE, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>View all</button>
        </div>
        <button onClick={() => { setTab("profile"); showToast("Insurance expires May 22"); }} data-testid="home-alert-insurance" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", background: "none", border: "none", width: "100%", color: "white", cursor: "pointer", fontFamily: apple, textAlign: "left" }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,107,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🛡</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "white", fontWeight: 600 }}>Insurance expires in 7 days</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>Tap to upload new document</div>
          </div>
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 16 }}>›</span>
        </button>
        <button onClick={() => { setTab("active"); showToast("Dispatch: 'Cleared for I-90 detour'"); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "0.5px solid rgba(255,255,255,0.06)", background: "none", border: "none", width: "100%", color: "white", cursor: "pointer", fontFamily: apple, textAlign: "left" }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,107,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>💬</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "white", fontWeight: 600 }}>New message from Dispatch</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>Tap to view conversation</div>
          </div>
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 16 }}>›</span>
        </button>
      </div>
    </div>
  );
}

function MiniStat({ icon, value, label, action, onClick }) {
  return (
    <button onClick={onClick} style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "10px 8px", textAlign: "left", color: "white", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <div style={{ fontSize: 14, marginBottom: 2 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: "white", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      <div style={{ fontSize: 8, color: "rgba(255,255,255,0.55)", lineHeight: 1.2 }}>{label}</div>
      <div style={{ fontSize: 9, color: ORANGE, fontWeight: 600, marginTop: 2 }}>{action}</div>
    </button>
  );
}

/* ---------- LOADS MARKETPLACE ---------- */
function LoadsTab({ loads, onPick, online, setOnline, setTab, showToast }) {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const filters = [
    { id: "All", icon: null },
    { id: "Nearby", icon: "📍" },
    { id: "Dry Van", icon: "📦" },
    { id: "Reefer", icon: "❄" },
    { id: "Flatbed", icon: "🚛" },
    { id: "Today", icon: "📅" },
  ];

  const filtered = useMemo(() => {
    let list = loads;
    if (filter !== "All" && filter !== "Nearby" && filter !== "Today") list = list.filter(l => l.trailer === filter);
    if (query) list = list.filter(l => `${l.from} ${l.to} ${l.trailer}`.toLowerCase().includes(query.toLowerCase()));
    return list;
  }, [filter, loads, query]);

  const trailerIcon = (t) => t === "Reefer" ? "❄" : t === "Flatbed" ? "🚛" : "📦";

  return (
    <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 900, color: ORANGE, letterSpacing: -0.5, fontStyle: "italic" }}>RY</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: "white", letterSpacing: 3 }}>DRIVER</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => showToast("2 new notifications")} data-testid="loads-bell" style={{ background: "none", border: "none", color: "white", cursor: "pointer", position: "relative", padding: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
            <span style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: ORANGE, border: "1.5px solid #0a0a0a" }} />
          </button>
          <button onClick={() => setTab("profile")} data-testid="loads-avatar" style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, #6b7280, #374151)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer", color: "white" }}>M</button>
        </div>
      </div>

      {/* Title row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 16px 12px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Loads</div>
        <button onClick={() => { setOnline(!online); showToast(online ? "Going offline" : "You're online"); }} data-testid="loads-online" style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "5px 8px 5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, fontFamily: apple, cursor: "pointer" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: online ? GREEN : "rgba(255,255,255,0.4)" }} />
          {online ? "Online" : "Offline"}
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>▾</span>
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: "0 16px 10px" }}>
        <div style={{ background: "rgba(255,255,255,0.05)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.5)"><path d="M10 4a6 6 0 014.8 9.6l5.3 5.3-1.4 1.4-5.3-5.3A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z" /></svg>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search route, city, or load type"
            data-testid="loads-search"
            style={{ background: "transparent", border: "none", outline: "none", color: "white", fontSize: 13, flex: 1, fontFamily: apple }}
          />
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ padding: "0 16px 10px", display: "flex", gap: 6, overflowX: "auto" }} className="scrollbar-hide">
        {filters.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id.toLowerCase().replace(/\s/g, "")}`}
              style={{
                background: "transparent",
                border: `0.5px solid ${active ? ORANGE : "rgba(255,255,255,0.15)"}`,
                color: active ? ORANGE : "white",
                padding: "7px 12px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: apple,
                cursor: "pointer",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 5,
                flexShrink: 0,
              }}
            >
              {f.icon && <span style={{ fontSize: 11 }}>{f.icon}</span>}
              {f.id}
            </button>
          );
        })}
        <button onClick={() => showToast("Filters · coming soon")} data-testid="loads-filters" style={{ background: "transparent", border: "0.5px solid rgba(255,255,255,0.15)", color: "white", padding: "7px 10px", borderRadius: 10, cursor: "pointer", position: "relative", display: "flex", alignItems: "center", flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M6 12h12M10 18h4" /></svg>
          <span style={{ position: "absolute", top: 2, right: 2, width: 6, height: 6, borderRadius: "50%", background: ORANGE }} />
        </button>
      </div>

      {/* Count + sort */}
      <div style={{ padding: "0 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}><span style={{ color: "white", fontWeight: 600 }}>{filtered.length}</span> loads available</div>
        <button onClick={() => showToast("Sort options · coming soon")} data-testid="loads-sort" style={{ background: "none", border: "none", color: "rgba(255,255,255,0.65)", fontSize: 11, fontFamily: apple, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
          Sorted by: <span style={{ color: ORANGE, fontWeight: 600 }}>Best Match</span>
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 9 }}>▾</span>
        </button>
      </div>

      {/* Load list */}
      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((l) => (
          <motion.button
            key={l.id}
            whileTap={{ scale: 0.985 }}
            onClick={() => onPick(l)}
            data-testid={`load-${l.id}`}
            style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 12, color: "white", cursor: "pointer", textAlign: "left", fontFamily: apple, position: "relative" }}
          >
            {/* Distance badge */}
            <div style={{ position: "absolute", top: 12, right: 12, display: "flex", alignItems: "center", gap: 4, color: ORANGE, fontSize: 11, fontWeight: 700 }}>
              <span style={{ width: 14, height: 14, borderRadius: "50%", background: "rgba(255,107,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8 }}>📍</span>
              {l.distAway}
            </div>

            {/* Lane row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 60 }}>
              <span style={{ fontSize: 18, opacity: 0.85 }}>{trailerIcon(l.trailer)}</span>
              <div style={{ fontSize: 15, fontWeight: 700, color: "white", letterSpacing: -0.2 }}>
                {l.from} <span style={{ color: "rgba(255,255,255,0.45)", margin: "0 4px" }}>→</span> {l.to}
              </div>
            </div>

            {/* Trailer chip + meta */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
              <span style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.08)", padding: "3px 8px", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 10 }}>{trailerIcon(l.trailer)}</span> {l.trailer}
              </span>
              <span style={{ color: "rgba(255,255,255,0.3)" }}>•</span>
              <span>{l.km} km</span>
              <span style={{ color: "rgba(255,255,255,0.3)" }}>•</span>
              <span>{l.lbs}</span>
            </div>

            {/* Pickup / Delivery + price row */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, alignItems: "flex-end" }}>
              <div style={{ display: "flex", gap: 24, flex: 1 }}>
                <div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>PICK UP</div>
                  <div style={{ fontSize: 12, color: "white", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ opacity: 0.6, fontSize: 10 }}>📅</span> {l.pickup}
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>{l.pickupDate}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>DELIVERY</div>
                  <div style={{ fontSize: 12, color: "white", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ opacity: 0.6, fontSize: 10 }}>📅</span> {l.delivery}
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>{l.deliveryDate}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)" }}>Starting at</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "white", letterSpacing: -0.3 }}>${l.price.toLocaleString()}</div>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
              <button onClick={(e) => { e.stopPropagation(); onPick(l); }} style={{ background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.1)", padding: "6px 18px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>Details</button>
              <button onClick={(e) => { e.stopPropagation(); onPick(l); }} style={{ background: ORANGE, color: "white", border: "none", padding: "6px 22px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: apple }}>Bid</button>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

/* ---------- LOAD DETAIL SHEET ---------- */
function LoadDetail({ load, onClose, onSubmitBid }) {
  const [bidMode, setBidMode] = useState(false);
  const aiSuggested = Math.round(load.current - 30);
  const [bid, setBid] = useState(aiSuggested);

  const bidFeedback = useMemo(() => {
    if (bid <= load.ceiling - 350) return { text: "Your bid is competitive", color: GREEN };
    if (bid >= load.ceiling - 50) return { text: "Your bid is currently too high", color: "#ef4444" };
    return { text: "Customer decision pending", color: "#f59e0b" };
  }, [bid, load.ceiling]);

  return (
    <motion.div
      initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 280 }}
      style={{ position: "absolute", left: 0, right: 0, top: 60, bottom: 0, background: "rgba(15,15,17,0.98)", backdropFilter: "blur(40px)", borderTopLeftRadius: 28, borderTopRightRadius: 28, padding: "10px 0 0", color: "white", fontFamily: apple, overflow: "hidden", display: "flex", flexDirection: "column", zIndex: 30 }}
    >
      <div style={{ width: 36, height: 4, background: "rgba(255,255,255,0.3)", borderRadius: 2, margin: "0 auto 8px" }} />
      <div style={{ padding: "0 20px 10px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, color: ORANGE, fontWeight: 700 }}>{load.id}</div>
          <div style={{ fontSize: 19, fontWeight: 700, marginTop: 2, letterSpacing: -0.3 }}>{load.from} → {load.to}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{load.mi.toLocaleString()} mi · {load.trailer}</div>
        </div>
        <button onClick={onClose} style={iconBtn} data-testid="load-detail-close">✕</button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "0 20px 20px" }}>
        {!bidMode ? (
          <>
            <Row k="PICKUP" v={load.pickup} c={GREEN} />
            <Row k="WEIGHT" v={load.weight} />
            <Row k="COMMODITY" v={load.commodity} />
            <Row k="STARTING PAYOUT" v={`$${load.current.toLocaleString()}`} accent />
            <div style={{ marginTop: 14, padding: 12, background: "rgba(255,255,255,0.04)", borderRadius: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>SPECIAL INSTRUCTIONS</div>
              <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>{load.instructions}</div>
            </div>
            <div style={{ marginTop: 12, padding: 12, background: "linear-gradient(135deg, rgba(255,107,0,0.12), rgba(255,107,0,0.04))", border: "0.5px solid rgba(255,107,0,0.3)", borderRadius: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: ORANGE, fontWeight: 700 }}>✦ AI ROUTE ESTIMATE</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 12 }}>
                <span style={{ color: "rgba(255,255,255,0.65)" }}>Fuel cost</span><span>≈ ${Math.round(load.mi * 0.42).toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 12 }}>
                <span style={{ color: "rgba(255,255,255,0.65)" }}>Drive time</span><span>{Math.round(load.mi / 55)} h</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 12 }}>
                <span style={{ color: "rgba(255,255,255,0.65)" }}>Suggested bid</span><span style={{ color: ORANGE, fontWeight: 700 }}>${aiSuggested.toLocaleString()}</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 13 }}>
              <span style={{ color: "rgba(255,255,255,0.55)" }}>Ceiling</span><span>${load.ceiling.toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 13, borderTop: "0.5px solid rgba(255,255,255,0.08)" }}>
              <span style={{ color: "rgba(255,255,255,0.55)" }}>Current low</span><span style={{ color: ORANGE }}>${load.current.toLocaleString()}</span>
            </div>
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>YOUR BID</div>
              <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: -1.5, marginTop: 4 }}>${bid.toLocaleString()}</div>
              <input
                type="range"
                min={load.ceiling - 800}
                max={load.ceiling}
                value={bid}
                onChange={(e) => setBid(Number(e.target.value))}
                style={{ width: "100%", accentColor: ORANGE, marginTop: 8 }}
                data-testid="bid-slider"
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "rgba(255,255,255,0.45)" }}>
                <span>floor</span><span>ceiling</span>
              </div>
            </div>
            <div style={{ marginTop: 14, padding: 12, background: `${bidFeedback.color}15`, border: `0.5px solid ${bidFeedback.color}50`, borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: bidFeedback.color }} />
              <div style={{ fontSize: 13, fontWeight: 500 }}>{bidFeedback.text}</div>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.45)" }}>
              AI suggestion based on your equipment, distance, fuel, and similar completed loads.
            </div>
          </>
        )}
      </div>
      <div style={{ padding: "12px 20px 22px", borderTop: "0.5px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.4)", display: "flex", gap: 10 }}>
        {!bidMode ? (
          <>
            <button onClick={onClose} style={secondaryBtn}>Decline</button>
            <button onClick={() => setBidMode(true)} style={secondaryBtnAccent} data-testid="open-bid">Submit bid</button>
            <button onClick={() => onSubmitBid(load, load.current)} style={primaryBtnTight} data-testid="accept-payout">Accept ${load.current.toLocaleString()}</button>
          </>
        ) : (
          <>
            <button onClick={() => setBidMode(false)} style={secondaryBtn}>Back</button>
            <button onClick={() => onSubmitBid(load, bid)} style={primaryBtnTight} data-testid="confirm-bid">Submit ${bid.toLocaleString()}</button>
          </>
        )}
      </div>
    </motion.div>
  );
}

function Row({ k, v, c, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", fontSize: 13, borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {c && <span style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />}
        <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>{k}</span>
      </div>
      <span style={{ fontWeight: accent ? 700 : 500, fontSize: accent ? 17 : 13, color: accent ? ORANGE : "white" }}>{v}</span>
    </div>
  );
}

/* ---------- ACTIVE LOAD ---------- */
const activeSteps = ["Assigned", "At Pickup", "Loaded", "In Transit", "Delivered"];
function ActiveTab({ activeLoad, step, setStep, online, setOnline, setTab, showToast }) {
  if (!activeLoad) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30, textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: 16, background: "rgba(255,107,0,0.12)", border: "0.5px solid rgba(255,107,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🛻</div>
        <div style={{ fontSize: 17, fontWeight: 600, marginTop: 14 }}>No active load</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 6, lineHeight: 1.5 }}>Accept or win a bid in the Loads tab to start hauling.</div>
      </div>
    );
  }

  const currentStep = Math.min(step, 4);
  const loadId = `RY-${activeLoad.id || "45872"}`;

  return (
    <div style={{ flex: 1, overflow: "auto", paddingBottom: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 900, color: ORANGE, letterSpacing: -0.5, fontStyle: "italic" }}>RY</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: "white", letterSpacing: 3 }}>DRIVER</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => showToast("2 new notifications")} data-testid="active-bell" style={{ background: "none", border: "none", color: "white", cursor: "pointer", position: "relative", padding: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
            <span style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: ORANGE, border: "1.5px solid #0a0a0a" }} />
          </button>
          <button onClick={() => setTab("profile")} data-testid="active-avatar" style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, #6b7280, #374151)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer", color: "white" }}>M</button>
        </div>
      </div>

      {/* Title */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 16px 14px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Active</div>
        <button onClick={() => { setOnline(!online); showToast(online ? "Going offline" : "You're online"); }} data-testid="active-online" style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "5px 8px 5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, fontFamily: apple, cursor: "pointer" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: online ? GREEN : "rgba(255,255,255,0.4)" }} />
            {online ? "Online" : "Offline"}
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>▾</span>
        </button>
      </div>

      {/* MAIN LOAD CARD */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ background: "rgba(16,185,129,0.12)", border: "0.5px solid rgba(16,185,129,0.4)", color: GREEN, padding: "4px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}>
            🚚 In Transit
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", display: "flex", alignItems: "center", gap: 4 }}>
            Load ID <span style={{ color: "white", fontWeight: 600 }}>{loadId}</span>
            <span style={{ opacity: 0.6, cursor: "pointer" }}>⎘</span>
          </div>
        </div>

        {/* Lane + payout */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "white", letterSpacing: -0.4 }}>
              Toronto, ON <span style={{ color: "rgba(255,255,255,0.45)", margin: "0 4px" }}>→</span> Chicago, IL
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 4, display: "flex", alignItems: "center", gap: 5 }}>
              🚚 Dry Van <span style={{ opacity: 0.4 }}>•</span> 640 km <span style={{ opacity: 0.4 }}>•</span> 14,000 lbs
            </div>
          </div>
          <div style={{ textAlign: "right", marginLeft: 10 }}>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.55)" }}>Payout</div>
            <div style={{ fontSize: 19, fontWeight: 800, color: "white", letterSpacing: -0.3 }}>$2,950</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)" }}>Rate: $2.10 / mile</div>
          </div>
        </div>

        {/* 3-col schedule */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 14 }}>
          <ScheduleCol label="PICK UP" date="May 15, 2025" time="2:00 PM EDT" />
          <ScheduleCol label="DELIVERY" date="May 16, 2025" time="8:00 AM CDT" />
          <ScheduleCol label="EST. ARRIVAL" date="May 16, 2025" time="On Time" timeColor={GREEN} />
        </div>

        {/* Status timeline */}
        <div style={{ marginTop: 18, padding: "0 4px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
            {activeSteps.map((s, i) => {
              const done = i < currentStep;
              const active = i === currentStep;
              return (
                <div key={s} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: done ? GREEN : active ? ORANGE : "transparent", border: done || active ? "none" : "1.5px solid rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: active ? 12 : 11, fontWeight: 700, boxShadow: active ? "0 0 0 4px rgba(255,107,0,0.2)" : "none" }}>
                    {done ? "✓" : active ? "🚚" : ""}
                  </div>
                  <div style={{ fontSize: 9, color: active ? ORANGE : "rgba(255,255,255,0.7)", fontWeight: active ? 700 : 500, textAlign: "center", whiteSpace: "nowrap" }}>{s}</div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
                    {i < currentStep ? ["May 15\n8:15 AM", "May 15\n1:30 PM", "May 15\n2:15 PM", "", ""][i] : i === currentStep ? "Current" : "—"}
                  </div>
                </div>
              );
            })}
            {/* connecting line */}
            <div style={{ position: "absolute", left: "10%", right: "10%", top: 11, height: 2, background: "rgba(255,255,255,0.12)", zIndex: 0 }} />
            <div style={{ position: "absolute", left: "10%", top: 11, height: 2, width: `${(currentStep / (activeSteps.length - 1)) * 80}%`, background: GREEN, zIndex: 0 }} />
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
          <ActionBtn icon="🗺" label="Open Route" onClick={() => showToast("Opening route in Maps…")} />
          <ActionBtn icon="💬" label="Message Dispatch" onClick={() => showToast("Dispatch chat opened")} />
          <ActionBtn icon="📞" label="Call Support" onClick={() => showToast("Calling 1-800-RY-XPRESS…")} />
        </div>
      </div>

      {/* SHIPMENT DETAILS */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 14 }}>📦</span>
          <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>Shipment Details</div>
        </div>
        {[
          ["Equipment", "Dry Van"],
          ["Weight", "14,000 lbs"],
          ["Reference #", "PO-77341"],
          ["Customer", "Global Retail Inc."],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "0.5px solid rgba(255,255,255,0.06)", fontSize: 13 }}>
            <span style={{ color: "rgba(255,255,255,0.6)" }}>{k}</span>
            <span style={{ color: "white", fontWeight: 500 }}>{v}</span>
          </div>
        ))}
        <button onClick={() => showToast("Note: Live unload")} data-testid="active-notes" style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "10px 0 2px", fontSize: 13, background: "none", border: "none", color: "white", cursor: "pointer", fontFamily: apple }}>
          <span style={{ color: "rgba(255,255,255,0.6)" }}>Notes</span>
          <span>Live unload <span style={{ color: "rgba(255,255,255,0.4)" }}>›</span></span>
        </button>
      </div>

      {/* ROUTE PREVIEW */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "white", marginBottom: 10 }}>Route Preview</div>
        <div style={{ position: "relative", height: 120, borderRadius: 12, background: "linear-gradient(135deg, #0e1f1a, #0a141a)", overflow: "hidden", border: "0.5px solid rgba(255,255,255,0.04)" }}>
          {/* Mock map */}
          <div style={{ position: "absolute", inset: 0, opacity: 0.4, backgroundImage: "linear-gradient(rgba(16,185,129,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.06) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 300 120" preserveAspectRatio="none">
            <path d="M30,80 Q 80,100 130,75 T 270,30" stroke="#10b981" strokeWidth="2.5" fill="none" />
          </svg>
          {/* Markers */}
          <div style={{ position: "absolute", left: 18, bottom: 22 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: ORANGE, border: "2px solid white", boxShadow: "0 0 0 3px rgba(255,107,0,0.2)" }} />
            <div style={{ fontSize: 11, color: "white", fontWeight: 600, marginTop: 4 }}>Chicago</div>
          </div>
          <div style={{ position: "absolute", right: 14, top: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <div style={{ fontSize: 11, color: "white", fontWeight: 600, marginBottom: 2 }}>Toronto</div>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "transparent", border: `2px solid ${GREEN}`, boxShadow: "0 0 0 3px rgba(16,185,129,0.2)" }} />
            </div>
          </div>
          {/* I-90 shield */}
          <div style={{ position: "absolute", left: "48%", top: "44%", background: "#1e3a8a", color: "white", fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, border: "1px solid white" }}>I-90</div>
        </div>
        <button onClick={() => showToast("Opening full route in Maps…")} data-testid="active-fullroute" style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "10px 0 2px", marginTop: 6, fontSize: 13, background: "none", border: "none", color: ORANGE, cursor: "pointer", fontFamily: apple, fontWeight: 600 }}>
          View Full Route
          <span>›</span>
        </button>
      </div>

      {/* REQUIRED DOCUMENTS */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 14 }}>📄</span>
          <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>Required Documents</div>
        </div>
        <DocLine label="BOL / Rate Confirmation" done />
        <DocLine label="POD (Upload after delivery)" done chip={{ text: "Pending", bg: "rgba(255,107,0,0.15)", color: ORANGE }} />
        <DocLine label="Trailer Photos (Optional)" done chip={{ text: "Optional", bg: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }} />
      </div>

      {/* RECENT ACTIVITY */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 14 }}>🕒</span>
          <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>Recent Activity</div>
        </div>
        {[
          { time: "2:15 PM", title: "Loaded at Shipper", sub: "Toronto, ON", active: true },
          { time: "1:30 PM", title: "Arrived at Pickup", sub: "Toronto, ON" },
          { time: "8:15 AM", title: "Load Assigned", sub: "RY Dispatch" },
        ].map((a, i, arr) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 4 }}>
              <div style={{ width: 9, height: 9, borderRadius: "50%", background: a.active ? ORANGE : "rgba(255,255,255,0.25)" }} />
              {i < arr.length - 1 && <div style={{ width: 1.5, height: 28, background: "rgba(255,255,255,0.12)", marginTop: 2 }} />}
            </div>
            <div style={{ flex: 1, paddingBottom: i < arr.length - 1 ? 8 : 0 }}>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", minWidth: 50 }}>{a.time}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "white" }}>{a.title}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>{a.sub}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mark step done (dev) */}
      <div style={{ padding: "0 14px" }}>
        <button onClick={() => setStep(Math.min(activeSteps.length, currentStep + 1))} style={{ width: "100%", background: "rgba(255,107,0,0.15)", border: "0.5px solid rgba(255,107,0,0.4)", color: ORANGE, padding: 12, borderRadius: 12, fontFamily: apple, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          Advance Status →
        </button>
      </div>
    </div>
  );
}

function ScheduleCol({ label, date, time, timeColor }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.55)", letterSpacing: 1, fontWeight: 600 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: 11, color: "white" }}>
        <span style={{ opacity: 0.6, fontSize: 9 }}>📅</span>
        {date}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2, fontSize: 11, color: timeColor || "white", fontWeight: timeColor ? 700 : 400 }}>
        {!timeColor && <span style={{ opacity: 0.6, fontSize: 9 }}>🕒</span>}
        {time}
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, onClick }) {
  return (
    <button onClick={onClick} style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "10px 6px", borderRadius: 12, fontSize: 10.5, fontWeight: 600, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
      <span style={{ color: ORANGE, fontSize: 12 }}>{icon}</span>
      {label}
    </button>
  );
}

function DocLine({ label, done, chip }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0" }}>
      <div style={{ width: 18, height: 18, borderRadius: "50%", background: done ? "rgba(16,185,129,0.18)" : "rgba(255,255,255,0.08)", border: `1px solid ${done ? GREEN : "rgba(255,255,255,0.2)"}`, display: "flex", alignItems: "center", justifyContent: "center", color: GREEN, fontSize: 10, fontWeight: 700 }}>
        {done ? "✓" : ""}
      </div>
      <div style={{ fontSize: 12, color: "white", flex: 1 }}>{label}</div>
      {chip && (
        <span style={{ background: chip.bg, color: chip.color, padding: "3px 9px", borderRadius: 6, fontSize: 10, fontWeight: 600 }}>{chip.text}</span>
      )}
    </div>
  );
}

/* ---------- EARNINGS ---------- */
function EarningsTab({ balance = 2340, cashOut, online, setOnline, setTab, showToast }) {
  const [period, setPeriod] = useState("Week");
  const days = [
    { d: "Mon", v: 720 }, { d: "Tue", v: 980 }, { d: "Wed", v: 1120 },
    { d: "Thu", v: 1340 }, { d: "Fri", v: 760 }, { d: "Sat", v: 0 }, { d: "Sun", v: 0 },
  ];
  const maxV = 1500;
  const pts = days.map((d, i) => {
    const x = (i / (days.length - 1)) * 100;
    const y = d.v ? 100 - (d.v / maxV) * 90 : 100;
    return { ...d, x, y };
  });
  const linePath = pts.filter(p => p.v).map((p, i) => `${i ? "L" : "M"}${p.x},${p.y}`).join(" ");
  const fillPath = linePath + ` L${pts[4].x},100 L${pts[0].x},100 Z`;

  return (
    <div style={{ flex: 1, overflow: "auto", paddingBottom: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 900, color: ORANGE, letterSpacing: -0.5, fontStyle: "italic" }}>RY</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: "white", letterSpacing: 3 }}>DRIVER</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => showToast("2 new notifications")} data-testid="earnings-bell" style={{ background: "none", border: "none", color: "white", cursor: "pointer", position: "relative", padding: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
            <span style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: ORANGE, border: "1.5px solid #0a0a0a" }} />
          </button>
          <button onClick={() => setTab("profile")} data-testid="earnings-avatar" style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, #6b7280, #374151)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer", color: "white" }}>M</button>
        </div>
      </div>

      {/* Online pill */}
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 16px 10px" }}>
        <button onClick={() => { setOnline(!online); showToast(online ? "Going offline" : "You're online"); }} data-testid="earnings-online" style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "5px 8px 5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, fontFamily: apple, cursor: "pointer" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: online ? GREEN : "rgba(255,255,255,0.4)" }} />{online ? "Online" : "Offline"}<span style={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>▾</span>
        </button>
      </div>

      {/* Balance hero card */}
      <div style={{ margin: "0 14px 12px", borderRadius: 18, padding: 16, border: `1px solid ${ORANGE}`, position: "relative", overflow: "hidden", background: "#0a0a0a" }}>
        <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "55%", backgroundImage: "url('https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&q=70')", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.65 }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, #0a0a0a 30%, rgba(10,10,10,0.4) 70%, transparent 100%)" }} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: "60%" }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", fontWeight: 700, letterSpacing: 1.5 }}>AVAILABLE BALANCE <span style={{ opacity: 0.5 }}>ⓘ</span></div>
          <div style={{ fontSize: 38, fontWeight: 800, color: "white", letterSpacing: -1.5, lineHeight: 1.05, marginTop: 4 }}>${balance.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ color: GREEN, fontSize: 10 }}>📅</span> Next payout: <strong style={{ color: "white" }}>Friday, May 17</strong>
          </div>
          <button onClick={cashOut} style={{ marginTop: 12, background: ORANGE, color: "white", border: "none", padding: "10px 14px", borderRadius: 10, fontFamily: apple, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, width: "100%", justifyContent: "space-between" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>⚡ Cash Out</span>
            <span>›</span>
          </button>
        </div>
      </div>

      {/* 4 stat tiles */}
      <div style={{ margin: "0 14px 12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <EarnStat icon="📈" label="THIS WEEK" value="$4,920" delta="8%" />
        <EarnStat icon="📅" label="THIS MONTH" value="$18,760" delta="12%" />
        <EarnStat icon="🚚" label="COMPLETED LOADS" value="28" delta="4" suffix=" vs last week" prefixDelta="+ " noPercent />
        <EarnStat icon="💵" label="AVG. PER LOAD" value="$1,245" delta="6%" />
      </div>

      {/* Earnings overview */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700 }}>EARNINGS OVERVIEW</div>
          <div style={{ display: "flex", background: "rgba(255,255,255,0.06)", borderRadius: 8, padding: 2, gap: 2 }}>
            {["Week", "Month", "Year"].map((t) => (
              <button key={t} onClick={() => { setPeriod(t); showToast(`${t} view`); }} data-testid={`period-${t.toLowerCase()}`} style={{ background: period === t ? ORANGE : "transparent", color: "white", border: "none", padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>{t}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.5 }}>$4,920</div>
          <div style={{ fontSize: 11, color: GREEN, fontWeight: 600 }}>↑ 8% vs last week</div>
        </div>

        {/* Chart */}
        <div style={{ position: "relative", marginTop: 12, height: 140 }}>
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: "visible" }}>
            <defs>
              <linearGradient id="earnFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ORANGE} stopOpacity="0.4" />
                <stop offset="100%" stopColor={ORANGE} stopOpacity="0" />
              </linearGradient>
            </defs>
            {[0, 1, 2, 3].map(i => (
              <line key={i} x1="0" y1={i * 25 + 10} x2="100" y2={i * 25 + 10} stroke="rgba(255,255,255,0.05)" strokeWidth="0.3" strokeDasharray="1 1" />
            ))}
            <path d={fillPath} fill="url(#earnFill)" />
            <path d={linePath} stroke={ORANGE} strokeWidth="0.8" fill="none" vectorEffect="non-scaling-stroke" />
            {pts.filter(p => p.v).map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="1.2" fill={ORANGE} />
            ))}
          </svg>
          {/* peak label */}
          <div style={{ position: "absolute", left: `${pts[3].x}%`, top: `${pts[3].y}%`, transform: "translate(-50%, -120%)", background: ORANGE, color: "white", fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 6, whiteSpace: "nowrap" }}>$1,340</div>
          {/* small value labels at each point */}
          {pts.filter(p => p.v).map((p, i) => i !== 3 && (
            <div key={i} style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -150%)", color: "rgba(255,255,255,0.7)", fontSize: 9 }}>${p.v}</div>
          ))}
          {/* x labels */}
          <div style={{ position: "absolute", left: 0, right: 0, bottom: -16, display: "flex", justifyContent: "space-between", fontSize: 9, color: "rgba(255,255,255,0.5)" }}>
            {days.map(d => <span key={d.d}>{d.d}</span>)}
          </div>
        </div>
      </div>

      {/* Earnings breakdown */}
      <div style={{ margin: "16px 14px 12px 14px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700 }}>EARNINGS BREAKDOWN <span style={{ opacity: 0.5 }}>ⓘ</span></div>
          <button onClick={() => showToast("Breakdown details opened")} data-testid="earnings-breakdown-details" style={{ background: "none", border: "none", color: ORANGE, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>View details ›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <BreakItem icon="🚚" label="Base Freight" value="$4,210" pct="66%" />
          <BreakItem icon="⭐" label="Bonuses" value="$980" pct="15%" />
          <BreakItem icon="📦" label="Accessorials" value="$420" pct="7%" />
          <BreakItem icon="➖" label="Deductions" value="-$690" pct="-11%" neg />
        </div>
        <div style={{ marginTop: 12, padding: 12, background: "rgba(255,107,0,0.06)", border: "0.5px solid rgba(255,107,0,0.25)", borderRadius: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", letterSpacing: 1, fontWeight: 600 }}>CASH OUT AVAILABLE</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "white", marginTop: 2 }}>${balance.toLocaleString()}</div>
          </div>
          <button onClick={cashOut} style={{ background: ORANGE, color: "white", border: "none", padding: "8px 14px", borderRadius: 10, fontFamily: apple, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            Cash Out <span>›</span>
          </button>
        </div>
      </div>

      {/* Recent payouts */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700 }}>RECENT PAYOUTS</div>
          <button onClick={() => showToast("All payouts opened")} data-testid="earnings-payouts-all" style={{ background: "none", border: "none", color: ORANGE, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>View all</button>
        </div>
        {[
          { date: "May 10, 2025", from: "Toronto, ON", to: "Chicago, IL", amt: "$2,950", status: "Paid", paid: true },
          { date: "May 8, 2025", from: "Hamilton, ON", to: "Buffalo, NY", amt: "$1,780", status: "Paid", paid: true },
          { date: "May 6, 2025", from: "Mississauga, ON", to: "Montreal, QC", amt: "$2,300", status: "Processing", paid: false },
        ].map((p, i) => (
          <button key={i} onClick={() => showToast(`Payout ${p.amt} · ${p.status}`)} data-testid={`payout-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "0.5px solid rgba(255,255,255,0.06)", background: "none", border: "none", width: "100%", color: "white", cursor: "pointer", fontFamily: apple, textAlign: "left" }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>$</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>{p.date}</div>
              <div style={{ fontSize: 12, color: "white", fontWeight: 500, marginTop: 1 }}>{p.from} <span style={{ color: "rgba(255,255,255,0.4)" }}>→</span> {p.to}</div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>{p.amt}</div>
            <span style={{ background: p.paid ? "rgba(16,185,129,0.12)" : "rgba(255,107,0,0.15)", color: p.paid ? GREEN : ORANGE, padding: "3px 9px", borderRadius: 6, fontSize: 10, fontWeight: 700, border: `0.5px solid ${p.paid ? "rgba(16,185,129,0.3)" : "rgba(255,107,0,0.3)"}` }}>{p.status}</span>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>›</span>
          </button>
        ))}
      </div>

      {/* Performance */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700 }}>YOUR PERFORMANCE</div>
          <button onClick={() => showToast("Performance breakdown")} data-testid="earnings-perf-details" style={{ background: "none", border: "none", color: ORANGE, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>View details</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <PerfItem icon={<RingIcon pct={84} />} value="84%" label="Acceptance Rate" delta="6%" />
          <PerfItem icon={<span style={{ fontSize: 20 }}>🕒</span>} value="97%" label="On-Time Rate" delta="3%" />
          <PerfItem icon={<span style={{ fontSize: 20 }}>📊</span>} value="$2.10" label="Avg. per Mile" delta="5%" />
          <PerfItem icon={<span style={{ fontSize: 20 }}>🏅</span>} value="4.9" label="Customer Rating" delta="0.1" noPercent />
        </div>
      </div>
    </div>
  );
}

function EarnStat({ icon, label, value, delta, prefixDelta = "+ ", noPercent }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 13, color: ORANGE }}>{icon}</span>
        <span style={{ fontSize: 9, letterSpacing: 1, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "white", letterSpacing: -0.3, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 10, color: GREEN, marginTop: 2 }}>
        <span>{prefixDelta}{delta}{!noPercent && delta.indexOf("%") < 0 ? "" : ""}</span>
        <span style={{ color: "rgba(255,255,255,0.5)" }}> vs last {label.includes("MONTH") ? "month" : "week"}</span>
      </div>
    </div>
  );
}

function BreakItem({ icon, label, value, pct, neg }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 13, color: ORANGE }}>{icon}</span>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>{label}</span>
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: neg ? "#ef4444" : "white", marginTop: 4, letterSpacing: -0.3 }}>{value}</div>
      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>{pct}</div>
    </div>
  );
}

function PerfItem({ icon, value, label, delta, noPercent }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div style={{ marginTop: 2 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "white", letterSpacing: -0.3 }}>{value}</div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>{label}</div>
        <div style={{ fontSize: 9, color: GREEN, marginTop: 2 }}>↑ {delta}{!noPercent ? "%" : ""} vs last week</div>
      </div>
    </div>
  );
}

function RingIcon({ pct }) {
  const r = 9;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <svg width="22" height="22" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r={r} stroke="rgba(255,255,255,0.12)" strokeWidth="2.5" fill="none" />
      <circle cx="12" cy="12" r={r} stroke={ORANGE} strokeWidth="2.5" fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" transform="rotate(-90 12 12)" />
    </svg>
  );
}

/* ---------- PROFILE ---------- */
function ProfileTab({ onLogout, online, setOnline, setTab, showToast }) {
  const toast = showToast || (() => {});
  return (
    <div style={{ flex: 1, overflow: "auto", paddingBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 900, color: ORANGE, letterSpacing: -0.5, fontStyle: "italic" }}>RY</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: "white", letterSpacing: 3 }}>DRIVER</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => toast("2 new notifications")} data-testid="profile-bell" style={{ background: "none", border: "none", color: "white", cursor: "pointer", position: "relative", padding: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
            <span style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: ORANGE, border: "1.5px solid #0a0a0a" }} />
          </button>
          <div style={{ width: 30, height: 30, borderRadius: "50%", border: `2px solid ${ORANGE}`, background: "linear-gradient(135deg, #6b7280, #374151)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>MT</div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 16px 12px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Profile</div>
        <button onClick={() => { setOnline(!online); toast(online ? "Going offline" : "You're online"); }} data-testid="profile-online" style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", padding: "5px 8px 5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, fontFamily: apple, cursor: "pointer" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: online ? GREEN : "rgba(255,255,255,0.4)" }} />{online ? "Online" : "Offline"}<span style={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>▾</span>
        </button>
      </div>

      {/* Hero profile card */}
      <div style={{ margin: "0 14px 12px", borderRadius: 18, padding: 14, border: `1px solid ${ORANGE}40`, background: "radial-gradient(circle at 50% 0%, rgba(255,107,0,0.08), transparent 60%), rgba(255,255,255,0.02)", position: "relative" }}>
        <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(16,185,129,0.15)", border: "0.5px solid rgba(16,185,129,0.4)", color: GREEN, padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>Approved</div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ position: "relative", width: 64, height: 64, flexShrink: 0 }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", border: `2px solid ${ORANGE}`, padding: 2, boxShadow: `0 0 20px ${ORANGE}40` }}>
              <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: "linear-gradient(135deg, #8b5e3c, #5a3d28)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: "white" }}>MT</div>
            </div>
            <div style={{ position: "absolute", bottom: -2, right: -2, width: 20, height: 20, borderRadius: "50%", background: GREEN, border: "2px solid #0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "white", fontWeight: 800 }}>✓</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "white", letterSpacing: -0.3 }}>Dwayne Johnson</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, marginTop: 3 }}>
              <span style={{ color: GREEN }}>✓</span>
              <span style={{ color: "rgba(255,255,255,0.85)" }}>Verified Driver</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, marginTop: 4, color: "rgba(255,255,255,0.75)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ color: ORANGE }}>★</span> <strong style={{ color: "white" }}>4.9</strong> Rating (128)</span>
              <span style={{ opacity: 0.4 }}>|</span>
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>📍 Toronto, ON</span>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>Profile <span style={{ color: ORANGE, fontWeight: 700 }}>92% complete</span></div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 3 }}>
            <div style={{ height: "100%", width: "92%", background: ORANGE, borderRadius: 3 }} />
          </div>
        </div>
        <button onClick={() => toast("Profile editor opened")} style={{ marginTop: 12, width: "100%", background: "transparent", border: `1px solid ${ORANGE}`, color: ORANGE, padding: 10, borderRadius: 10, fontFamily: apple, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          Manage Profile <span>›</span>
        </button>
      </div>

      {/* Personal + Truck */}
      <div style={{ margin: "0 14px 12px" }}>
        <ProfCard title="Personal Info" icon="👤">
          <ProfRow icon="📞" v="(647) 555-0198" />
          <ProfRow icon="✉" v="mike.thompson@email.com" />
          <ProfRow icon="🪪" k="Driver ID" v="RY-45872" />
        </ProfCard>
      </div>
      <div style={{ margin: "0 14px 12px" }}>
        <ProfCard title="Truck & Equipment" icon="🚚">
          <ProfRow k="Truck Type" v="Freightliner Cascadia" />
          <ProfRow k="Trailer Type" v="53' Dry Van" />
          <ProfRow k="Plate / Unit ID" v="PA-1287 / 7783" />
          <ProfRow k="Capacity" v="26,000 lbs" />
        </ProfCard>
      </div>

      {/* Documents */}
      <div style={{ margin: "0 14px 12px" }}>
        <ProfCard title="Documents" icon="📄" action="View all" onAction={() => toast("All documents opened")}>
          <DocLine2 icon="🚚" label="Driver Licence" status="Verified" />
          <DocLine2 icon="🛡" label="Insurance" status="Expires in 12 days" warn />
          <DocLine2 icon="📋" label="Safety Certificate" status="Verified" />
          <DocLine2 icon="📑" label="Vehicle Registration" status="Uploaded" />
        </ProfCard>
      </div>

      {/* Availability */}
      <div style={{ margin: "0 14px 12px" }}>
        <ProfCard title="Availability & Preferences" icon="⚙">
          <ProfRow k="Preferred Loads" v="Dry Van, Reefer" />
          <ProfRow k="Service Regions" v="ON, QC, MI, OH, PA" />
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 12 }}>
            <span style={{ color: "rgba(255,255,255,0.65)" }}>Availability</span>
            <span style={{ background: "rgba(16,185,129,0.12)", border: "0.5px solid rgba(16,185,129,0.4)", color: GREEN, padding: "2px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>Available</span>
          </div>
          <button onClick={() => toast("Preferences updated")} style={{ marginTop: 8, width: "100%", background: "transparent", border: `1px solid ${ORANGE}`, color: ORANGE, padding: 9, borderRadius: 10, fontFamily: apple, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Update Preferences</button>
        </ProfCard>
      </div>

      {/* Payment & Payout */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,107,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: ORANGE, fontWeight: 800 }}>$</span>
          <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>Payment & Payout</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>Payout Method</div>
            <div style={{ fontSize: 12, color: "white", marginTop: 2 }}>Direct Deposit •••• 8421</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 8 }}>Next Payout</div>
            <div style={{ fontSize: 12, color: "white", marginTop: 2 }}>May 17, 2025</div>
          </div>
          <button onClick={() => toast("Payout settings opened")} style={{ background: ORANGE, color: "white", border: "none", padding: "10px 14px", borderRadius: 10, fontFamily: apple, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>Manage Payouts ›</button>
        </div>
      </div>

      {/* Support & Settings */}
      <div style={{ margin: "0 14px 12px", background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, color: ORANGE }}>⚙</span>
            <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>Support & Settings</div>
          </div>
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>›</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
          <SupportItem icon="👤" label="Account Settings" onClick={() => toast("Account settings opened")} />
          <SupportItem icon="🔔" label="Notifications" onClick={() => toast("Notification preferences")} />
          <SupportItem icon="?" label="Help Center" onClick={() => toast("Help center · 24/7")} />
          <SupportItem icon="↗" label="Sign Out" accent onClick={onLogout} />
        </div>
      </div>
    </div>
  );
}

function ProfCard({ title, icon, action, onAction, children }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14, color: ORANGE }}>{icon}</span>
          <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>{title}</div>
        </div>
        {action ? (
          <button onClick={onAction} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontFamily: apple, padding: 0 }}>
            <span style={{ fontSize: 11, color: ORANGE, fontWeight: 600 }}>{action}</span>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>›</span>
          </button>
        ) : (
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>›</span>
        )}
      </div>
      {children}
    </div>
  );
}
function ProfRow({ icon, k, v }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.65)" }}>
        {icon && <span style={{ color: ORANGE, fontSize: 13, width: 16, textAlign: "center" }}>{icon}</span>}
        {k && <span>{k}</span>}
      </div>
      <span style={{ color: "white", fontWeight: 500, textAlign: "right" }}>{v}</span>
    </div>
  );
}
function DocLine2({ icon, label, status, warn }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: ORANGE, fontSize: 13, width: 18, textAlign: "center" }}>{icon}</span>
        <span style={{ color: "white" }}>{label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 5, color: warn ? ORANGE : GREEN, fontWeight: 600 }}>
        <span>{warn ? "🕒" : "✓"}</span>
        {status}
      </div>
    </div>
  );
}
function SupportItem({ icon, label, accent, onClick }) {
  return (
    <button onClick={onClick} style={{ background: "transparent", border: "none", color: accent ? ORANGE : "white", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: 4 }}>
      <span style={{ fontSize: 16, color: ORANGE }}>{icon}</span>
      <span style={{ fontSize: 10, fontWeight: 500, textAlign: "center", lineHeight: 1.2 }}>{label}</span>
    </button>
  );
}
function Section({ title, children }) { return <div>{children}</div>; }
function DocRow() { return null; }

/* ============================================================ */
/*  SHARED PRIMITIVES                                           */
/* ============================================================ */
function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "10px 12px", marginTop: 10 }}>
      <div style={{ fontSize: 9, letterSpacing: 1.5, color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>{label}</div>
      <input
        type={type}
        value={v}
        onChange={(e) => { setV(e.target.value); onChange && onChange(e.target.value); }}
        placeholder={placeholder}
        data-testid={testid}
        style={{ background: "transparent", border: "none", outline: "none", color: "white", fontSize: 15, fontFamily: apple, width: "100%", marginTop: 2, padding: 0 }}
      />
    </div>
  );
}
function Pill({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{ background: active ? "rgba(255,107,0,0.16)" : "rgba(255,255,255,0.04)", border: `0.5px solid ${active ? "rgba(255,107,0,0.5)" : "rgba(255,255,255,0.1)"}`, color: active ? ORANGE : "rgba(255,255,255,0.85)", padding: "8px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: apple, cursor: "pointer" }}>
      {label}
    </button>
  );
}
const primaryBase = { background: ORANGE, color: "white", border: "none", padding: "14px 16px", borderRadius: 14, fontSize: 15, fontWeight: 700, fontFamily: apple, cursor: "pointer", width: "100%" };
function PrimaryBtn({ children, onClick, testid }) {
  return <motion.button whileTap={{ scale: 0.97 }} onClick={onClick} data-testid={testid} style={primaryBase}>{children}</motion.button>;
}
function SecondaryBtn({ children, onClick, testid }) {
  return (
    <motion.button whileTap={{ scale: 0.97 }} onClick={onClick} data-testid={testid}
      style={{ background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.1)", padding: "14px 16px", borderRadius: 14, fontSize: 15, fontWeight: 600, fontFamily: apple, cursor: "pointer", width: "100%" }}>
      {children}
    </motion.button>
  );
}
const iconBtn = { width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,0.06)", border: "none", color: "white", fontSize: 18, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center" };
const secondaryBtn = { flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "none", padding: "12px 14px", borderRadius: 12, fontSize: 13, fontWeight: 600, fontFamily: apple, cursor: "pointer" };
const secondaryBtnAccent = { flex: 1, background: "rgba(255,107,0,0.12)", color: ORANGE, border: "0.5px solid rgba(255,107,0,0.4)", padding: "12px 14px", borderRadius: 12, fontSize: 13, fontWeight: 700, fontFamily: apple, cursor: "pointer" };
const primaryBtnTight = { flex: 1.4, background: ORANGE, color: "white", border: "none", padding: "12px 14px", borderRadius: 12, fontSize: 13, fontWeight: 700, fontFamily: apple, cursor: "pointer" };
const chipBtn = { background: "rgba(255,255,255,0.08)", color: "white", border: "none", padding: "8px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: apple, cursor: "pointer" };
